import React, { useCallback, useEffect, useId, useRef } from 'react';
import NavGroupAccordion from '@/components/navigation/NavGroupAccordion';
import NotificationCenter from '@/components/NotificationCenter';
import RuntimeStatusBar from '@/components/RuntimeStatusBar';
import SidebarIdentity from '@/components/SidebarIdentity';
import WorkspaceSwitcher from '@/components/WorkspaceSwitcher';
import BrandTagline from '@/components/BrandTagline';
import BrandWord from '@/components/BrandWord';
import { useNavigation } from '@/context/NavigationContext';
import { clearAccessToken } from '@/api/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMe } from '@/context/MeContext';
import BrandMark from '@/components/BrandMark';
import { IconLogout } from '@/components/Icons';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

function useBodyScrollLock(locked: boolean) {
  useEffect(() => {
    if (!locked) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [locked]);
}

export default function MobileDrawer() {
  const drawerId = useId();
  const location = useLocation();
  const navigate = useNavigate();
  const { me } = useMe();
  const showRuntime = Boolean(me?.permissions.canViewSystemDashboard || me?.permissions.canViewRuntime);
  const {
    navGroups,
    drawerOpen,
    openDrawer,
    closeDrawer,
    openCommandPalette,
  } = useNavigation();
  const drawerRef = useRef<HTMLElement>(null);

  useBodyScrollLock(drawerOpen);

  useEffect(() => {
    closeDrawer();
  }, [location.pathname, closeDrawer]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDrawer();
        return;
      }
      if (event.key !== 'Tab' || !drawerRef.current) return;
      const focusables = Array.from(drawerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeDrawer, drawerOpen]);

  useEffect(() => {
    if (!drawerOpen || !drawerRef.current) return;
    const first = drawerRef.current.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();
  }, [drawerOpen]);

  const handleNavigate = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  return (
    <>
      <header className="mobile-nav-topbar" data-testid="mobile-nav-topbar">
        <button
          type="button"
          className="mobile-nav-menu-btn"
          aria-label="Open navigation menu"
          aria-expanded={drawerOpen}
          aria-controls={drawerId}
          onClick={openDrawer}
        >
          <span className="mobile-nav-menu-icon" aria-hidden>
            ☰
          </span>
        </button>
        <div className="mobile-nav-brand">
          <BrandWord className="mobile-nav-brand-name" />
        </div>
        <div className="mobile-nav-actions">
          {showRuntime ? <RuntimeStatusBar compact /> : null}
          <NotificationCenter />
        </div>
      </header>

      {drawerOpen ? (
        <button
          type="button"
          className="mobile-nav-overlay"
          aria-label="Close navigation menu"
          onClick={closeDrawer}
          data-testid="mobile-nav-overlay"
        />
      ) : null}

      {drawerOpen ? (
        <aside
          id={drawerId}
          ref={drawerRef}
          className="mobile-nav-drawer mobile-nav-drawer--open"
          aria-hidden={false}
          aria-label="Navigation menu"
          data-testid="mobile-nav-drawer"
          onTouchStart={(event) => {
            const touch = event.touches[0];
            (event.currentTarget as HTMLElement & { _touchStartX?: number })._touchStartX = touch.clientX;
          }}
          onTouchEnd={(event) => {
            const startX = (event.currentTarget as HTMLElement & { _touchStartX?: number })._touchStartX;
            const endX = event.changedTouches[0]?.clientX;
            if (startX != null && endX != null && endX - startX < -56) {
              closeDrawer();
            }
          }}
        >
          <div className="mobile-nav-drawer-header">
            <div className="brand brand--compact">
              <BrandMark />
              <div>
                <BrandWord className="brand-name" />
                <BrandTagline />
              </div>
            </div>
            <button type="button" className="mobile-nav-close-btn" aria-label="Close menu" onClick={closeDrawer}>
              ×
            </button>
          </div>

          {me ? <SidebarIdentity /> : null}

          <div className="mobile-nav-workspace">
            <WorkspaceSwitcher sectionLabel="Current workspace" />
          </div>

          <div className="mobile-nav-drawer-tools">
            <button type="button" className="btn btn-ghost btn-block mobile-nav-command-btn" onClick={openCommandPalette}>
              Search navigation…
            </button>
            <div className="theme-toggle theme-toggle--static" aria-label="Theme">
              <span>Dark theme</span>
              <span className="theme-toggle__pill">Active</span>
            </div>
          </div>

          <NavGroupAccordion
            navGroups={navGroups}
            variant="mobile"
            linkClassName="nav-link"
            onNavigate={handleNavigate}
            className="mobile-nav-groups"
            data-testid="mobile-nav-groups"
          />

          <div className="mobile-nav-drawer-footer">
            <button
              type="button"
              className="btn btn-ghost btn-block mobile-nav-signout"
              onClick={() => {
                clearAccessToken();
                navigate('/login', { replace: true });
              }}
            >
              <IconLogout className="nav-icon" />
              Sign out
            </button>
          </div>
        </aside>
      ) : null}
    </>
  );
}
